import React, { useEffect, useRef, useState } from "react"
import { ChatCircle, Handbag, Heart, X } from "@phosphor-icons/react"
import { Dialog, DialogPanel } from "@headlessui/react"

import CheckBox from "@components/common/CheckBox"
import { StoreContext } from "@context/StoreContext"
import LookBookItem from "@components/LookBook/LookBookItem"
import IndicatorItem from "@components/LookBook/IndicatorItem"
import Divider from "@components/common/Divider"

const LookBookModal = ({ data, isOpen = false, onClose = () => {} }) => {
	const containerRef = useRef(null)
	const { addVariantsToCartBulk, loading } = React.useContext(StoreContext)
	const [productSelected, setProductSelected] = useState([])
	const [selectAll, setSelectAll] = useState(false)
	const [total, setTotal] = useState(0)

	useEffect(() => {
		if (isOpen) setSelectAll(true)
	}, [isOpen])

	const handleSelectAll = () => {
		setSelectAll((prev) => !prev)
	}

	const addToBag = async () => {
		await addVariantsToCartBulk(productSelected)
		setProductSelected([])
		setSelectAll(true)
		onClose()
	}

	useEffect(() => {
		const totalAmount = productSelected.reduce((sum, variant) => {
			const price = parseFloat(variant?.price?.amount || 0)
			return sum + price
		}, 0)

		setTotal(totalAmount)
	}, [productSelected])

	const handleClose = () => {
		setProductSelected([])
		setSelectAll(false)
		onClose()
	}

	const formattedDate = new Date(
		data?.rawResponse?.dateCreated
	).toLocaleDateString("en-US", {
		month: "long",
		day: "numeric",
	})

	const handleScrollToBottom = (item) => {
		if(containerRef.current) {
			const offsetScroll = Math.abs(containerRef?.current?.scrollTop - item?.top)
			console.info("current pos:",Math.floor(item?.top), "| item top pos:", offsetScroll)
			containerRef?.current?.scrollTo({
				top: offsetScroll + containerRef.current.scrollTop,
				behavior: "smooth",
			})
		}
	}

	return (
		<React.Fragment>
			<Dialog open={isOpen} onClose={handleClose} className="relative z-[55]">
				<div
					id="lookbook-modal"
					className="fixed inset-0 w-screen overflow-y-auto p-0 md:p-4 bg-black bg-opacity-60"
				>
					<div className="flex min-h-full items-center justify-center">
						<DialogPanel className="relative space-y-2 border bg-white p-6 w-screen md:w-auto h-full max-w-[836px] gap-4 minimalist-scrollbar">
							<div className="flex flex-nowrap justify-end items-center">
								<X
									className="absolute top-0 right-0 cursor-pointer text-black m-1"
									size={24}
									weight="light"
									onClick={handleClose}
								/>
							</div>

							<section className="grid grid-cols-7 w-full gap-8">
								<div
									className={`relative col-span-7 ${
										data?.rawResponse?.tags?.length > 0 ? "lg:col-span-4" : ""
									}`}
								>
									<div className="relative overflow-hidden top-0 left-0 w-full max-w-[486px] max-h-[612px]">
										<div className="absolute w-full h-full z-20">
											{data?.rawResponse?.tags.map((tag, index) => {
												return (
													<IndicatorItem
														key={`$indicator-${index}`}
														data={tag}
														style={{ top: `${tag.y}%`, left: `${tag.x}%` }}
														placeTop={tag.y > 70}
														placeLeft={tag.x > 42}
													/>
												)
											})}
										</div>
										{data?.rawResponse?.mediaType === 1 ? (
											<img
												src={data?.image}
												alt="lookbook-modal-preview"
												className="object-cover w-full z-10"
											/>
										) : (
											<video
												className="object-cover w-full z-10"
												src={data?.rawResponse?.videoUrl}
												width="100%"
												controls=""
												autoplay=""
											></video>
										)}
									</div>
									<div className="flex justify-between w-full p-2 text-xs">
										<div className="">
											<div className="text-sm font-semibold">
												{data?.rawResponse?.username}
											</div>
											<div className="text-sm text-secondary">
												{formattedDate}
											</div>
										</div>
										<div className="flex flex-nowrap justify-center gap-x-4">
											<div className="flex flex-col items-center gap-1">
												<Heart weight="fill" size={12} />
												<div>{data?.rawResponse?.countLikes}</div>
											</div>
											<div className="flex flex-col items-center gap-1">
												<ChatCircle weight="fill" size={12} />
												<div>{data?.rawResponse?.countComments}</div>
											</div>
										</div>
									</div>
								</div>

								{data?.rawResponse?.tags?.length > 0 ? (
									<div className="relative col-span-7 lg:col-span-3 pr-2 flex flex-col justify-between w-full float-right lg:float-none">
										{/* <p className="text-[#3D3D3D] mb-4">
											Love It? Grab It. All in One Go!
										</p> */}
										<CheckBox
											checked={selectAll}
											className="text-sm"
											label={"Love It? Grab It. All in One Go!"}
											onChange={() => handleSelectAll()}
										/>
										<section
											ref={containerRef}
											className="relative w-full overflow-hidden h-full md:h-[400px] overflow-y-auto lg:overflow-y-scroll lg:scrollbar-hide mt-4"
										>
											<div className="flex flex-col gap-4 relative">
												{data?.rawResponse?.tags?.map((item, index) => (
													<div key={`lb-item-${item?.productId}-${index}`}>
														{index !== 0 && (
															<Divider className="flex items-center justify-center mt-4 mb-8" />
														)}
														<LookBookItem
															data={item}
															selectAll={selectAll}
															productSelected={productSelected}
															setProductSelected={setProductSelected}
															scrollRef={handleScrollToBottom}
														/>
													</div>
												))}
											</div>
										</section>

										<div className="w-full sticky lg:relative bg-white pt-4 space-y-2 left-0 -bottom-4 pb-4 lg:pb-0 z-20">
											<div className="flex flex-nowrap justify-between items-center">
												<span className="text-secondary text-md font-medium">
													Grab {data?.rawResponse?.tags?.length > 1 ? 'These' : 'This'} Look
													{data?.rawResponse?.tags?.length > 1 && "s"} for Only:
												</span>
												<span className="text-[24px] font-semibold text-primary">
													{process.env.GATSBY_STORE !== "PH" ? "$" : "₱"}
													{total}
												</span>
											</div>
											<button
												disabled={loading || productSelected.length === 0}
												className="btn-primary flex gap-x-2 w-full text-sm justify-center"
												onClick={addToBag}
											>
												<Handbag size={20} weight="light" />
												<span>Add to Bag</span>
											</button>
											<p className="text-xs font-medium text-[#888888] text-center">
												Double check your size before adding to bag.
											</p>
										</div>
									</div>
								) : null}
							</section>
						</DialogPanel>
					</div>
				</div>
			</Dialog>
		</React.Fragment>
	)
}

export default LookBookModal
